// smartreader-frontend/src/services/api.js
import axios from 'axios';
import { parseError } from '../api/error';
import i18n from 'i18next';

const API_KEY = process.env.REACT_APP_API_KEY;
const RAW_BASE_URL = process.env.REACT_APP_API_URL || '';
const BASE_URL = `${RAW_BASE_URL.replace(/\/?api\/?$/, '')}/api`;

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Request interceptor for API key injection
// JWT tokens are sent automatically via httpOnly cookies — no localStorage tokens
api.interceptors.request.use(
  (config) => {
    if (API_KEY) {
      config.headers['x-api-key'] = API_KEY;
    }
    // O `Content-Type: application/json` acima é default da instância e vale
    // também para upload. No axios 1.x isso não é só um cabeçalho errado: o
    // `transformRequest` vê JSON declarado e SERIALIZA o FormData com
    // `formDataToJSON`, então o arquivo nunca sai do navegador e o servidor
    // recebe `{}`. Foi assim que a tela de APK respondia 500 ("Cannot read
    // properties of undefined (reading 'path')") — o multer não via multipart
    // nenhum. Removendo o cabeçalho, o navegador escreve o
    // `multipart/form-data` com o boundary certo.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor for error handling + session expiry detection
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // The i18next SINGLETON, not `../i18n/config`. Importing the config here
    // would make the HTTP layer pull in (and run) the whole i18n bootstrap —
    // which breaks every suite that mocks react-i18next, since config.js calls
    // `.use(initReactI18next)` at module load. The singleton is initialised by
    // that config at app startup; before then `t` simply returns the key.
    //
    // Resolved at call time, not module load: the user can switch language
    // after the interceptor is installed.
    const t = (key) => i18n.t(key, { ns: 'errors' });
    error.apiError = parseError(error.response);
    if (error.response) {
      const status = error.response.status;

      // Session expired (unauthenticated) — notify AuthContext to logout.
      // 403 is authenticated-but-forbidden (RBAC denial, e.g. a scoped role
      // like report-viewer hitting an out-of-scope endpoint) and must NOT log
      // the user out, or any allow-list gap kicks them back to the login page.
      if (status === 401) {
        window.dispatchEvent(new Event('auth:expired'));
        error.userMessage = t('authentication.sessionExpired');
      } else if (status >= 400 && status < 500) {
        // Deliberately NOT `error.apiError?.message`. That is the NestJS
        // exception text: always English, and written for developers — e.g.
        // 'The "from" date must be before or equal to the "to" date'. It used
        // to be forwarded straight to the operator, so any 4xx leaked
        // untranslated copy into a pt-BR screen.
        //
        // `userMessage` is the app's generic error channel (107 call sites);
        // a caller that needs specificity reads the STRUCTURED payload and
        // translates it itself — see AnalysisReportsPage, which uses
        // `err.apiError.details.currentStatus` with `t('statuses.' + status)`.
        // The raw string stays available on `error.apiError.message` for
        // logging and for that kind of deliberate use.
        error.userMessage = t('messages.requestFailed');
      } else if (status >= 500) {
        error.userMessage = t('network.serverError');
      }
    } else {
      error.userMessage = t('network.connectionFailed');
    }
    return Promise.reject(error);
  },
);

export const getBaseUrlWithoutApi = () => {
  const baseURL = api.defaults.baseURL || '';
  // Robustly remove a trailing "/api" or "/api/" if present
  return baseURL.replace(/\/api\/?$/, '');
};
export default api;
